import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ProjectProgress } from "@/hooks/use-project-progress";

/** Color class based on progress percentage */
function progressColor(pct: number): string {
  if (pct >= 100) return "bg-success";
  if (pct >= 60) return "bg-primary";
  if (pct >= 30) return "bg-[var(--sr-sitron)]";
  return "bg-foreground/30";
}

/**
 * Shared progress bar showing approved task hours vs budgeted hours.
 * Reusable across ProjectsPage, ProjectDetailPage, and /mine-oppgaver.
 */
export function ProjectProgressBar({
  progress,
  showLabel = false,
  className,
}: {
  progress: ProjectProgress | undefined;
  /** Show "X% (Xt / Yt)" label next to the bar */
  showLabel?: boolean;
  className?: string;
}) {
  if (!progress || progress.assignedHours === 0) {
    return <span className="text-[11px] text-muted-foreground/40">—</span>;
  }

  const { approvedHours, assignedHours, budgetedHours, pct } = progress;

  const bar = (
    <div className="h-1.5 w-full bg-muted overflow-hidden">
      <div
        className={cn("h-full transition-all", progressColor(pct))}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );

  const tooltipText =
    `${pct}% godkjent — ${approvedHours.toFixed(1)}t av ${assignedHours.toFixed(1)}t tildelt` +
    (budgetedHours > 0 ? ` (${budgetedHours.toFixed(1)}t budsjettert)` : "");

  if (showLabel) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex-1">{bar}</div>
          </TooltipTrigger>
          <TooltipContent className="text-xs">{tooltipText}</TooltipContent>
        </Tooltip>
        <span className="text-[11px] tabular-nums text-muted-foreground whitespace-nowrap">
          {pct}%
        </span>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{bar}</TooltipTrigger>
      <TooltipContent className="text-xs">{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

export default ProjectProgressBar;
